import React from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import PropTypes from 'prop-types';

import { Alert } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Progress } from 'shadcn/components/ui/Progress';

const STATUS_LABELS = {
    pending: 'Pending',
    queued: 'Queued',
    executing: 'Executing',
    succeeded: 'Succeeded',
    failed: 'Failed',
};

function getPalletStatusIcon(status) {
    if (status === 'succeeded') {
        return <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" aria-hidden="true" />;
    }
    if (status === 'failed') {
        return <XCircle className="h-4 w-4 text-red-600 shrink-0" aria-hidden="true" />;
    }
    return <Loader2 className="h-4 w-4 text-muted-foreground shrink-0 animate-spin" aria-hidden="true" />;
}

function getPalletRowClassName(status) {
    if (status === 'succeeded') {
        return 'text-green-800';
    }
    if (status === 'failed') {
        return 'text-red-800';
    }
    return 'text-muted-foreground';
}

function BulkMovePalletsSummary({ batchProgress, error = null, onClose }) {
    const { completed = 0, failed = 0, pallets = [], status, succeeded = 0, total = 0 } = batchProgress || {};
    const isCompleted = status === 'completed';
    const isTerminal = isCompleted || Boolean(error);
    const progressValue = total > 0 ? (completed / total) * 100 : 0;

    return (
        <div className="p-6 flex flex-col items-center gap-6">
            <div className="w-full space-y-4">
                <div className="space-y-2">
                    <p className="text-sm font-medium text-center">
                        {completed} of {total} complete
                    </p>
                    <Progress value={progressValue} aria-label="Bulk move progress" />
                    <p className="text-xs text-center text-muted-foreground">
                        {succeeded} succeeded · {failed} failed
                        {!isTerminal ? ' · Moving pallets…' : ''}
                    </p>
                </div>
                {error ? <p className="text-red-600 text-sm text-center">{error}</p> : null}

                {(succeeded > 0 || failed > 0) && (
                    <div className="space-y-2">
                        {succeeded > 0 && (
                            <Alert variant="default" className="bg-green-50 border-green-200 text-green-500">
                                <CheckCircle2 className="h-4 w-4" />
                                <p className="text-xs text-green-800">
                                    <span className="font-semibold">Success:</span> {succeeded} Pallet
                                    {succeeded !== 1 ? 's' : ''} moved successfully
                                </p>
                            </Alert>
                        )}
                        {failed > 0 && (
                            <Alert variant="default" className="bg-red-50 border-red-200 text-red-500">
                                <XCircle className="h-4 w-4" />
                                <p className="text-xs text-red-800">
                                    <span className="font-semibold">Failed:</span> {failed} Pallet
                                    {failed !== 1 ? 's' : ''} failed to move
                                </p>
                            </Alert>
                        )}
                    </div>
                )}

                <ul className="w-full max-h-64 overflow-y-auto space-y-2 border rounded-md p-3">
                    {pallets.map((pallet) => {
                        const statusLabel = STATUS_LABELS[pallet.status] || pallet.status;
                        return (
                            <li
                                key={pallet.palletId}
                                className={`flex items-start gap-2 text-xs ${getPalletRowClassName(pallet.status)}`}
                            >
                                {getPalletStatusIcon(pallet.status)}
                                <div>
                                    <p>
                                        <span className="font-medium">Pallet {pallet.palletId}</span>
                                        {' — '}
                                        <span className="capitalize">{statusLabel}</span>
                                    </p>
                                    {pallet.status === 'failed' && pallet.result ? (
                                        <p className="mt-0.5">{pallet.result}</p>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
            {isTerminal ? (
                <Button variant="default" onClick={onClose}>
                    Close
                </Button>
            ) : null}
        </div>
    );
}

BulkMovePalletsSummary.propTypes = {
    batchProgress: PropTypes.shape({
        batchId: PropTypes.string,
        completed: PropTypes.number,
        failed: PropTypes.number,
        pallets: PropTypes.arrayOf(
            PropTypes.shape({
                palletId: PropTypes.number.isRequired,
                result: PropTypes.string,
                status: PropTypes.string.isRequired,
            }),
        ),
        status: PropTypes.string,
        succeeded: PropTypes.number,
        total: PropTypes.number,
    }).isRequired,
    error: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

BulkMovePalletsSummary.defaultProps = {
    error: null,
};

export { BulkMovePalletsSummary };
